import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { warehouseApi } from '@api/warehouseApi';
import type {
  StockFilters,
  ReceiptItem,
  CreateShipmentRequest,
  CreateAdjustmentRequest,
  CreateLocationRequest,
} from '@/types/warehouse';

export function useStock(mdFilters: StockFilters) {
  return useQuery({
    queryKey: ['warehouse', 'stock', mdFilters],
    queryFn: () => warehouseApi.getStock(mdFilters),
    placeholderData: (mdPreviousData) => mdPreviousData,
  });
}

export function useProductStock(mdProductId: string | undefined) {
  return useQuery({
    queryKey: ['warehouse', 'stock', 'product', mdProductId],
    queryFn: () => warehouseApi.getProductStock(mdProductId!),
    enabled: !!mdProductId,
  });
}

export function useExpiringBatches(mdDays: number) {
  return useQuery({
    queryKey: ['warehouse', 'expiring', mdDays],
    queryFn: () => warehouseApi.getExpiringBatches(mdDays),
  });
}

export function useLocations() {
  return useQuery({
    queryKey: ['warehouse', 'locations'],
    queryFn: () => warehouseApi.getLocations(),
  });
}

export function useBatchOperations(mdBatchId: string | undefined) {
  return useQuery({
    queryKey: ['warehouse', 'batch-operations', mdBatchId],
    queryFn: () => warehouseApi.getBatchOperations(mdBatchId!),
    enabled: !!mdBatchId,
    // Маршруту ще немає на сервері — не повторюємо 404
    retry: false,
  });
}

export function useFefoPreview(mdProductId: string | undefined, mdQuantity: number | undefined) {
  return useQuery({
    queryKey: ['warehouse', 'fefo-preview', mdProductId, mdQuantity],
    queryFn: () => warehouseApi.getFefoPreview(mdProductId!, mdQuantity!),
    enabled: !!mdProductId && !!mdQuantity && mdQuantity > 0,
    // Маршруту ще немає на сервері — не повторюємо 404
    retry: false,
  });
}

export function useCreateReceipt() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdItems: ReceiptItem[]) => warehouseApi.createReceipt(mdItems),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['warehouse', 'stock'] });
      message.success('Оприбуткування успішно створено');
    },
    onError: () => {
      message.error('Помилка при оприбуткуванні');
    },
  });
}

export function useCreateShipment() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateShipmentRequest) => warehouseApi.createShipment(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['warehouse', 'stock'] });
      message.success('Відвантаження успішно створено');
    },
    onError: () => {
      message.error('Помилка при відвантаженні');
    },
  });
}

export function useCreateAdjustment() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateAdjustmentRequest) => warehouseApi.createAdjustment(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['warehouse', 'stock'] });
      message.success('Коригування успішно збережено');
    },
    onError: () => {
      message.error('Помилка при коригуванні');
    },
  });
}

export function useCreateLocation() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateLocationRequest) => warehouseApi.createLocation(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['warehouse', 'locations'] });
      message.success('Локацію створено');
    },
    onError: () => {
      message.error('Помилка при створенні локації');
    },
  });
}
